import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowLeft,
  Compass,
  Crown,
  Star,
  Sparkles,
  Calendar,
  Heart,
  Briefcase,
  Check,
  Loader2,
  Zap,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

const PremiumPage = () => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await api.get("/premium/plans");
        setPlans(response.data.plans);
      } catch (error) {
        console.error("Error fetching plans:", error);
        toast.error("Failed to load plans");
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  // Handle return from Stripe checkout
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      setCheckingPayment(true);
      const checkPayment = async () => {
        try {
          const response = await api.get(`/premium/status/${sessionId}`);
          if (response.data.status === "paid") {
            toast.success("Premium activated! Welcome to the inner circle.");
            await checkAuth(); // Refresh user data
            navigate("/premium/features", { replace: true });
          } else if (response.data.status === "expired") {
            toast.error("Payment session expired. Please try again.");
          }
        } catch (error) {
          console.error("Error checking payment:", error);
        } finally {
          setCheckingPayment(false);
        }
      };
      checkPayment();
    }
  }, [searchParams, checkAuth, navigate]);

  const handleSubscribe = async (planId) => {
    setProcessingPlan(planId);
    try {
      const response = await api.post("/premium/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Failed to start checkout. Please try again.");
    } finally {
      setProcessingPlan(null);
    }
  };

  if (loading || checkingPayment) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#070B14" }}>
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-white/70 font-body">
            {checkingPayment ? "Verifying your payment..." : "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  // If already premium, redirect to features
  if (user?.is_premium) {
    return (
      <div className="min-h-screen" style={{ background: "#070B14" }} data-testid="premium-active">
        <header className="sticky top-0 z-50 bg-[#070B14]/70 backdrop-blur-xl border-b border-white/5">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/dashboard")}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A16207] flex items-center justify-center">
                <Crown className="w-4 h-4 text-[#070B14]" />
              </div>
              <span className="font-display text-xl text-white">Premium</span>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div className="text-center space-y-6 animate-fade-in-up">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A16207] flex items-center justify-center">
              <Crown className="w-10 h-10 text-[#070B14]" />
            </div>
            <h1 className="font-display text-4xl text-white">You're Premium!</h1>
            <p className="text-white/60 font-body max-w-md mx-auto">
              Unlock the mysteries of the cosmos with your exclusive premium features
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4 pt-8">
              <Button
                onClick={() => navigate("/premium/birth-chart")}
                className="btn-gold py-6 text-lg"
              >
                <Star className="w-5 h-5 mr-2" />
                Birth Chart Reading
              </Button>
              <Button
                onClick={() => navigate("/premium/weekly-forecast")}
                className="btn-secondary py-6 text-lg"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Weekly Forecast
              </Button>
              <Button
                onClick={() => navigate("/premium/monthly-forecast")}
                className="btn-secondary py-6 text-lg"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Monthly Forecast
              </Button>
              <Button
                onClick={() => navigate("/premium/compatibility")}
                className="btn-secondary py-6 text-lg"
              >
                <Heart className="w-5 h-5 mr-2" />
                Compatibility
              </Button>
              <Button
                onClick={() => navigate("/premium/tarot")}
                className="btn-gold py-6 text-lg"
                data-testid="premium-tarot-btn"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Tarot Readings
              </Button>
              <Button
                onClick={() => navigate("/chat")}
                className="btn-secondary py-6 text-lg"
                data-testid="premium-chat-btn"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                AI Chat Advisor
              </Button>
              <Button
                onClick={() => navigate("/dashboard")}
                className="btn-secondary py-6 text-lg sm:col-span-2"
              >
                <Compass className="w-5 h-5 mr-2" />
                Daily Guidance
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#070B14" }} data-testid="premium-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#070B14]/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A16207] flex items-center justify-center">
              <Compass className="w-4 h-4 text-[#070B14]" />
            </div>
            <span className="font-display text-xl text-white">Premium</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Hero */}
        <div className="text-center space-y-4 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
            <Crown className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-sm text-[#D4AF37] font-body">Unlock Your Cosmic Blueprint</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white">
            Discover Your <span className="gold-text">Destiny</span>
          </h1>
          <p className="text-white/60 font-body text-base sm:text-lg max-w-2xl mx-auto">
            Get personalized birth chart readings, weekly horoscopes, star sign compatibility, 
            and deeper spiritual insights powered by AI astrology.
          </p>
        </div>

        {/* Features Preview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
          {[
            { icon: Star, label: "Birth Chart", desc: "Full natal reading" },
            { icon: Calendar, label: "Weekly Forecast", desc: "7-day horoscope" },
            { icon: Heart, label: "Compatibility", desc: "Love matches" },
            { icon: Zap, label: "Tarot & More", desc: "AI card readings" },
          ].map((feature) => (
            <div key={feature.label} className="glass-card p-4 text-center">
              <feature.icon className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
              <h3 className="text-white font-body font-medium text-sm">{feature.label}</h3>
              <p className="text-white/50 text-xs font-body">{feature.desc}</p>
            </div>
          ))}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-4 sm:gap-6 animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`glass-card p-6 sm:p-8 relative ${
                plan.id === "yearly" ? "border-[#D4AF37] ring-1 ring-[#D4AF37]/50" : ""
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.save && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#D4AF37] text-[#070B14] text-xs font-body font-medium">
                  SAVE {plan.save}
                </div>
              )}
              
              <div className="text-center mb-6">
                <h3 className="font-display text-xl text-white mb-2">{plan.name}</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="font-display text-4xl text-white">${plan.price}</span>
                  <span className="text-white/50 font-body">/{plan.duration}</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-white/80 font-body text-sm">
                    <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Button
                data-testid={`subscribe-${plan.id}`}
                onClick={() => handleSubscribe(plan.id)}
                disabled={processingPlan === plan.id}
                className={`w-full py-6 text-lg ${
                  plan.id === "yearly" ? "btn-gold" : "btn-secondary"
                }`}
              >
                {processingPlan === plan.id ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    {plan.id === "yearly" ? "Best Value" : "Subscribe"}
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>

        {/* What You Get */}
        <div className="gold-accent-card p-6 sm:p-8 animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
          <h2 className="font-display text-2xl text-white mb-6 text-center">
            What's Included in Premium
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Star className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-white font-body font-medium">Complete Birth Chart</h4>
                  <p className="text-white/60 text-sm font-body">Sun, Moon, Rising signs plus full personality analysis</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-white font-body font-medium">Weekly Horoscope</h4>
                  <p className="text-white/60 text-sm font-body">Love, career, health forecasts with lucky days</p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Heart className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-white font-body font-medium">Love Compatibility</h4>
                  <p className="text-white/60 text-sm font-body">Deep analysis of any star sign pairing</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Briefcase className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-white font-body font-medium">Life Path Numerology</h4>
                  <p className="text-white/60 text-sm font-body">Your destiny number and what it means</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonial */}
        <div className="text-center animate-fade-in-up" style={{ animationDelay: "0.4s" }}>
          <p className="text-white/40 text-sm font-body italic">
            "SoulGuide Premium changed how I understand myself. The birth chart reading was incredibly accurate."
          </p>
          <p className="text-white/30 text-xs font-body mt-2">- Sarah M., Premium Member</p>
        </div>
      </main>
    </div>
  );
};

export default PremiumPage;
